import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { FileKey, ShieldAlert, Trash2, Clock, Lock } from 'lucide-react';
import { Card, CardContent } from '../../components/ui/Card.js';
import { Badge } from '../../components/ui/Badge.js';
import { Button } from '../../components/ui/Button.js';
import { CopyButton } from '../../components/ui/CopyButton.js';
import { Modal, ConfirmDialog } from '../../components/ui/Modal.js';
import { JsonViewer } from '../../components/ui/JsonViewer.js';
import { TableSkeleton } from '../../components/ui/Skeleton.js';
import { EmptyState, ErrorState } from '../../components/ui/EmptyState.js';
import { PermissionGuard } from '../../components/security/PermissionGuard.js';
import { useToast } from '../../context/ToastContext.js';
import { useI18n } from '../../context/I18nContext.js';
import { fetchSnapshots, inspectSnapshot, deleteSnapshot } from '../../api/snapshots.js';
import { formatDate, formatTimeRemaining, truncateId } from '../../lib/utils.js';
import type { SupportSnapshot } from '@hisob/types';
import type { DecryptedSnapshotData } from '../../types/index.js';

export function SnapshotsPage() {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { success, error: toastError } = useToast();
  const { t } = useI18n();

  const [snapshotToInspect, setSnapshotToInspect] = useState<SupportSnapshot | null>(null);
  const [accessReason, setAccessReason] = useState('');
  const [decryptedData, setDecryptedData] = useState<DecryptedSnapshotData | null>(null);
  const [snapshotToDelete, setSnapshotToDelete] = useState<SupportSnapshot | null>(null);

  const { data: snapshots, isLoading, error, refetch } = useQuery({
    queryKey: ['admin-snapshots'],
    queryFn: fetchSnapshots,
  });

  const inspectMutation = useMutation({
    mutationFn: () => inspectSnapshot(snapshotToInspect!.id, accessReason.trim()),
    onSuccess: (data) => {
      setDecryptedData(data);
      setSnapshotToInspect(null);
      setAccessReason('');
      success('Diagnostic snapshot decrypted. Access logged to immutable audit trail.');
    },
    onError: (err: any) => {
      toastError(err.message || 'Failed to inspect snapshot', err.requestId);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => deleteSnapshot(id),
    onSuccess: () => {
      success(t('snapshots.deleteSuccess'));
      setSnapshotToDelete(null);
      queryClient.invalidateQueries({ queryKey: ['admin-snapshots'] });
      queryClient.invalidateQueries({ queryKey: ['overview-metrics'] });
    },
    onError: (err: any) => {
      toastError(err.message || 'Failed to delete snapshot', err.requestId);
    },
  });

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div>
          <h2 className="text-xl font-bold tracking-tight text-slate-900 dark:text-slate-100 flex items-center gap-2">
            <FileKey className="w-5 h-5 text-rose-500" />
            {t('snapshots.title')}
          </h2>
          <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">
            {t('snapshots.description')}
          </p>
        </div>
      </div>

      {/* Critical Privacy Banner */}
      <div className="p-4 rounded-xl border border-rose-300 dark:border-rose-900/60 bg-rose-50/50 dark:bg-rose-950/20 text-rose-950 dark:text-rose-200 text-xs flex items-start gap-3 shadow-xs">
        <ShieldAlert className="w-5 h-5 text-rose-600 dark:text-rose-400 shrink-0 mt-0.5" />
        <div className="space-y-1">
          <span className="font-bold block text-sm">Privileged Privacy-Restricted Workspace</span>
          <p className="leading-relaxed text-slate-600 dark:text-slate-300">
            Snapshots are uploaded <strong>only after explicit in-app user consent</strong> for active support investigations. All decryptions require an auditable business reason and are permanently tracked in the compliance log. Snapshots auto-expire after 24 hours.
          </p>
        </div>
      </div>

      {/* Table */}
      <Card>
        <CardContent className="p-0 overflow-x-auto">
          {isLoading ? (
            <div className="p-6">
              <TableSkeleton rows={3} cols={6} />
            </div>
          ) : error ? (
            <div className="p-6">
              <ErrorState title={t('snapshots.loadError')} message={(error as any).message} onRetry={() => refetch()} />
            </div>
          ) : !snapshots || snapshots.length === 0 ? (
            <div className="p-8">
              <EmptyState
                title={t('snapshots.emptyTitle')}
                description={t('snapshots.emptyDesc')}
              />
            </div>
          ) : (
            <table className="w-full text-left text-xs">
              <thead className="bg-slate-50 dark:bg-slate-900/60 border-b border-slate-200 dark:border-slate-800 text-slate-500 font-semibold uppercase tracking-wider">
                <tr>
                  <th className="py-3 px-4">{t('snapshots.tableId')}</th>
                  <th className="py-3 px-4">{t('snapshots.tableDeviceId')}</th>
                  <th className="py-3 px-4">App Version</th>
                  <th className="py-3 px-4">Storage</th>
                  <th className="py-3 px-4">{t('snapshots.tableExpiresIn')}</th>
                  <th className="py-3 px-4">{t('snapshots.tableCreatedAt')}</th>
                  <th className="py-3 px-4 text-right">{t('snapshots.tableActions')}</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                {snapshots.map((snp) => {
                  const nowSec = Math.floor(Date.now() / 1000);
                  const isExpired = snp.expires_at <= nowSec;

                  return (
                    <tr key={snp.id} className="hover:bg-slate-50 dark:hover:bg-slate-900/40">
                      <td className="py-3 px-4">
                        <CopyButton value={snp.id} truncate={true} className="font-mono text-rose-600 dark:text-rose-400 font-semibold" />
                      </td>
                      <td className="py-3 px-4">
                        <button
                          onClick={() => navigate(`/admin/devices/${snp.device_id}`)}
                          className="font-mono text-slate-700 dark:text-slate-300 hover:text-indigo-600 hover:underline"
                        >
                          {truncateId(snp.device_id, 8)}
                        </button>
                      </td>
                      <td className="py-3 px-4 font-mono text-slate-600 dark:text-slate-400">
                        v{snp.app_version}
                      </td>
                      <td className="py-3 px-4 uppercase font-mono text-[11px] text-slate-500">
                        {snp.storage_type}
                      </td>
                      <td className="py-3 px-4">
                        {isExpired ? (
                          <Badge variant="danger">Expired</Badge>
                        ) : (
                          <Badge variant="warning">
                            <Clock className="w-3 h-3 text-amber-500" />
                            {formatTimeRemaining(snp.expires_at)}
                          </Badge>
                        )}
                      </td>
                      <td className="py-3 px-4 text-slate-500">
                        {formatDate(snp.created_at)}
                      </td>
                      <td className="py-3 px-4 text-right">
                        <div className="flex items-center justify-end gap-1">
                          <PermissionGuard permission="snapshots:read">
                            <Button
                              size="sm"
                              variant="outline"
                              className="text-indigo-600 dark:text-indigo-400 hover:bg-indigo-50 border-indigo-200"
                              onClick={() => {
                                setSnapshotToInspect(snp);
                                setAccessReason('');
                              }}
                            >
                              <Lock className="w-3 h-3 mr-1" /> {t('snapshots.inspectBtn')}
                            </Button>
                          </PermissionGuard>

                          <PermissionGuard permission="snapshots:delete">
                            <Button
                              size="sm"
                              variant="outline"
                              className="text-rose-600 hover:bg-rose-50 border-rose-200"
                              onClick={() => setSnapshotToDelete(snp)}
                            >
                              <Trash2 className="w-3 h-3" />
                            </Button>
                          </PermissionGuard>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </CardContent>
      </Card>

      {/* Mandatory Audit Reason Dialog before Decrypting */}
      {snapshotToInspect && (
        <Modal
          isOpen={Boolean(snapshotToInspect)}
          onClose={() => setSnapshotToInspect(null)}
          title={t('snapshots.inspectModalTitle')}
          maxWidth="md"
        >
          <div className="space-y-4 text-xs">
            <p className="text-slate-600 dark:text-slate-400">
              {t('snapshots.inspectPrompt')}
            </p>
            <div className="space-y-1">
              <label className="font-semibold text-slate-700 dark:text-slate-300">
                Reason <span className="text-rose-500">*</span>
              </label>
              <textarea
                rows={3}
                value={accessReason}
                onChange={(e) => setAccessReason(e.target.value)}
                placeholder={t('snapshots.reasonPlaceholder')}
                required
                className="w-full rounded-lg border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 p-2 text-xs focus:ring-2 focus:ring-indigo-500"
              />
            </div>

            <div className="flex justify-end gap-2 pt-3 border-t border-slate-100 dark:border-slate-800">
              <Button variant="ghost" onClick={() => setSnapshotToInspect(null)}>
                {t('common.cancel')}
              </Button>
              <Button
                variant="primary"
                disabled={!accessReason.trim() || inspectMutation.isPending}
                isLoading={inspectMutation.isPending}
                onClick={() => inspectMutation.mutate()}
              >
                {t('snapshots.submitInspect')}
              </Button>
            </div>
          </div>
        </Modal>
      )}

      {/* Decrypted Payload Viewer */}
      {decryptedData && (
        <Modal
          isOpen={Boolean(decryptedData)}
          onClose={() => setDecryptedData(null)}
          title="Decrypted Diagnostic Archive Payload"
          maxWidth="2xl"
        >
          <div className="space-y-4 text-xs">
            <JsonViewer data={decryptedData} />
            <div className="flex justify-end pt-3 border-t border-slate-100 dark:border-slate-800">
              <Button variant="outline" onClick={() => setDecryptedData(null)}>
                {t('common.close')}
              </Button>
            </div>
          </div>
        </Modal>
      )}

      {/* Delete Confirmation */}
      {snapshotToDelete && (
        <ConfirmDialog
          isOpen={Boolean(snapshotToDelete)}
          onClose={() => setSnapshotToDelete(null)}
          onConfirm={() => deleteMutation.mutate(snapshotToDelete.id)}
          title={t('snapshots.deleteModalTitle')}
          message={t('snapshots.deleteModalMessage')}
          confirmText={t('common.delete')}
          variant="danger"
          isLoading={deleteMutation.isPending}
        />
      )}
    </div>
  );
}
